import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, Layers, Flag, Check, X, CalendarClock } from "lucide-react";
import { adminClient, notifyProductsChanged } from "./api";
import ImageUploader from "./ImageUploader";

const EMPTY_BANNER = { title: "", subtitle: "", kind: "department", department_id: "", bg_color: "#0C2E2B", bg_color_2: "#1a5850", text_color: "#FFFFFF", accent_color: "#FFDF00", image_url: "", image_style: "side", size: "half", sort_order: 0, is_active: true, starts_at: "", ends_at: "" };

const PRESETS = [
  { name: "Forest", bg_color: "#0C2E2B", bg_color_2: "#1a5850", text_color: "#FFFFFF", accent_color: "#FFDF00" },
  { name: "Sunshine", bg_color: "#FFDF00", bg_color_2: "#F5C400", text_color: "#0C2E2B", accent_color: "#0C2E2B" },
  { name: "Rust", bg_color: "#7C2D12", bg_color_2: "#EA580C", text_color: "#FFFFFF", accent_color: "#FFDF00" },
  { name: "Charcoal", bg_color: "#18181B", bg_color_2: "#3F3F46", text_color: "#FFFFFF", accent_color: "#FFDF00" },
  { name: "Ocean", bg_color: "#0C4A6E", bg_color_2: "#0284C7", text_color: "#FFFFFF", accent_color: "#FDE68A" },
  { name: "Cream", bg_color: "#FAF8F5", bg_color_2: "#E7E5E4", text_color: "#0C2E2B", accent_color: "#EA580C" },
];

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" data-testid={`${testId}-picker`} value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="w-9 h-9 rounded border border-zinc-300 p-0.5 bg-white" />
        <Input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#0C2E2B" className="h-9 font-mono-data text-xs" />
      </div>
    </div>
  );
}

function BannerPreview({ b, deptName }) {
  const c2 = b.bg_color_2 || b.bg_color;
  return (
    <div className="relative overflow-hidden rounded-xl p-5 min-h-[120px] flex flex-col justify-between" style={{ background: `linear-gradient(120deg, ${b.bg_color} 0%, ${c2} 100%)`, color: b.text_color }} data-testid="banner-preview">
      {b.image_url && (b.image_style === "cover"
        ? <><img src={b.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${b.bg_color} 0%, ${b.bg_color}E6 35%, ${c2}40 100%)` }} /></>
        : <img src={b.image_url} alt="" className="absolute inset-y-0 right-0 w-[55%] h-full object-cover [mask-image:linear-gradient(to_right,transparent,black_40%)]" />)}
      <div className="relative font-tactical text-[10px] uppercase tracking-[0.3em]" style={{ color: b.accent_color }}>{b.kind === "trending" ? "Hot right now" : "Department"}</div>
      <div className="relative">
        <div className="font-display font-black text-2xl leading-none">{b.title || "Banner title"}</div>
        {b.subtitle && <div className="text-xs opacity-80 mt-1">{b.subtitle}</div>}
        <div className="inline-block mt-3 text-[11px] font-bold px-3 py-1 rounded-full" style={{ background: b.accent_color, color: b.bg_color }}>Shop {b.kind === "trending" ? "the drop" : deptName || b.title || "…"} →</div>
      </div>
    </div>
  );
}

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
export function scheduleState(b) {
  if (!b.starts_at && !b.ends_at) return null;
  const t = today();
  if (b.starts_at && b.starts_at > t) return { label: `Starts ${b.starts_at}`, cls: "bg-sky-100 text-sky-900" };
  if (b.ends_at && b.ends_at < t) return { label: `Ended ${b.ends_at}`, cls: "bg-zinc-200 text-zinc-600" };
  return { label: `Live until ${b.ends_at || "—"}`, cls: "bg-emerald-100 text-emerald-900" };
}

export default function MerchLayoutAdmin({ onChanged }) {
  const [depts, setDepts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [bannerOpen, setBannerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BANNER);

  const load = async () => {
    try {
      const [{ data: d }, { data: b }] = await Promise.all([adminClient.get("/admin/merch/departments"), adminClient.get("/admin/merch/banners")]);
      setDepts(d.items || []);
      setBanners(b.items || []);
    } catch { toast.error("Could not load departments"); }
  };
  useEffect(() => { load(); }, []);
  const changed = () => { notifyProductsChanged(); load(); onChanged?.(); };
  const deptName = (id) => depts.find((d) => d.id === id)?.name;

  const addDept = async (e) => {
    e.preventDefault();
    if (!newDept.trim()) return;
    try {
      const { data } = await adminClient.post("/admin/merch/departments", { name: newDept.trim() });
      const style = PRESETS[(depts.length + 2) % PRESETS.length];
      await adminClient.post("/admin/merch/banners", { ...EMPTY_BANNER, ...style, title: data.name, subtitle: `Shop ${data.name.toLowerCase()}`, kind: "department", department_id: data.id });
      toast.success(`"${data.name}" added with its own banner`);
      setNewDept("");
      changed();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
  };
  const saveRename = async (d) => {
    if (!renameVal.trim()) return;
    try {
      await adminClient.put(`/admin/merch/departments/${d.id}`, { ...d, name: renameVal.trim() });
      const linked = banners.filter((b) => b.department_id === d.id && b.title === d.name);
      await Promise.all(linked.map((b) => adminClient.put(`/admin/merch/banners/${b.id}`, { ...b, title: renameVal.trim() })));
      toast.success("Renamed");
      setRenaming(null);
      changed();
    } catch (err) { toast.error(err?.response?.data?.detail || "Rename failed"); }
  };
  const removeDept = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? Its banner is removed and ${d.product_count} product(s) become unassigned.`)) return;
    try { await adminClient.delete(`/admin/merch/departments/${d.id}`); toast.success("Department removed"); changed(); }
    catch { toast.error("Delete failed"); }
  };
  const moveDept = async (idx, dir) => {
    const next = [...depts];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    await Promise.all(next.map((d, i) => adminClient.put(`/admin/merch/departments/${d.id}`, { ...d, sort_order: i })));
    changed();
  };
  const toggleDept = async (d) => { await adminClient.put(`/admin/merch/departments/${d.id}`, { ...d, is_active: !d.is_active }); changed(); };

  const openBanner = (b) => {
    setEditing(b || null);
    setForm(b ? { ...EMPTY_BANNER, ...b, department_id: b.department_id || "", starts_at: b.starts_at || "", ends_at: b.ends_at || "" } : { ...EMPTY_BANNER, sort_order: banners.length + 1 });
    setBannerOpen(true);
  };
  const saveBanner = async (e) => {
    e.preventDefault();
    const payload = { ...form, department_id: form.kind === "trending" ? null : form.department_id || null, sort_order: parseInt(form.sort_order, 10) || 0, starts_at: form.starts_at || null, ends_at: form.ends_at || null };
    if (payload.kind === "department" && !payload.department_id) return toast.error("Pick the department this banner opens");
    try {
      if (editing) await adminClient.put(`/admin/merch/banners/${editing.id}`, payload);
      else await adminClient.post("/admin/merch/banners", payload);
      toast.success(editing ? "Banner updated · live now" : "Banner added · live now");
      setBannerOpen(false);
      changed();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const removeBanner = async (b) => {
    if (!window.confirm(`Remove banner "${b.title}"?`)) return;
    try { await adminClient.delete(`/admin/merch/banners/${b.id}`); toast.success("Banner removed"); changed(); } catch { toast.error("Delete failed"); }
  };
  const moveBanner = async (idx, dir) => {
    const next = [...banners];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    await Promise.all(next.map((b, i) => adminClient.put(`/admin/merch/banners/${b.id}`, { ...b, sort_order: i })));
    changed();
  };
  const toggleBanner = async (b) => { await adminClient.put(`/admin/merch/banners/${b.id}`, { ...b, is_active: !b.is_active }); changed(); };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-5" data-testid="merch-layout-admin">
      <Card className="p-5 border-2 border-zinc-200 xl:col-span-2" data-testid="departments-card">
        <div className="flex items-center gap-2 mb-1"><Layers className="w-4 h-4 text-[#0C2E2B]" /><h3 className="font-display font-black text-[#0C2E2B]">Departments</h3></div>
        <p className="text-xs text-zinc-500 mb-4">Tabs customers use to browse (Mens, Womens, Fan Gear…). Adding one also creates its banner.</p>
        <form onSubmit={addDept} className="flex gap-2 mb-4">
          <Input data-testid="dept-new-input" value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="New department, e.g. Fan Gear" className="h-10" />
          <Button data-testid="dept-add-btn" type="submit" className="btn-kinetic rounded-md h-10"><Plus className="w-4 h-4" /></Button>
        </form>
        <div className="space-y-2">
          {depts.map((d, i) => (
            <div key={d.id} data-testid={`dept-row-${d.id}`} className={`flex items-center gap-2 rounded-md border px-3 py-2 ${d.is_active ? "border-zinc-200 bg-white" : "border-zinc-200 bg-zinc-50 opacity-70"}`}>
              {renaming === d.id ? (
                <>
                  <Input data-testid={`dept-rename-input-${d.id}`} value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-8 flex-1" autoFocus onKeyDown={(e) => e.key === "Enter" && saveRename(d)} />
                  <Button size="sm" className="h-8 btn-kinetic rounded-md" data-testid={`dept-rename-save-${d.id}`} onClick={() => saveRename(d)}><Check className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setRenaming(null)}><X className="w-3.5 h-3.5" /></Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#0C2E2B] truncate" data-testid={`dept-name-${d.id}`}>{d.name}</div>
                    <div className="text-[10px] text-zinc-500">{d.product_count} product{d.product_count === 1 ? "" : "s"}{!d.is_active && " · hidden"}</div>
                  </div>
                  <Switch data-testid={`dept-toggle-${d.id}`} checked={d.is_active} onCheckedChange={() => toggleDept(d)} />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveDept(i, -1)} disabled={i === 0} data-testid={`dept-up-${d.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveDept(i, 1)} disabled={i === depts.length - 1} data-testid={`dept-down-${d.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid={`dept-rename-${d.id}`} onClick={() => { setRenaming(d.id); setRenameVal(d.name); }}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600" data-testid={`dept-delete-${d.id}`} onClick={() => removeDept(d)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
          ))}
          {depts.length === 0 && <p className="text-xs text-zinc-500">No departments yet.</p>}
        </div>
      </Card>

      <Card className="p-5 border-2 border-zinc-200 xl:col-span-3" data-testid="banners-card">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2"><Flag className="w-4 h-4 text-[#0C2E2B]" /><h3 className="font-display font-black text-[#0C2E2B]">Banners</h3></div>
          <Button data-testid="banner-add-btn" onClick={() => openBanner(null)} className="btn-kinetic rounded-md text-xs h-9"><Plus className="w-4 h-4 mr-1" /> New banner</Button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Big tiles on the merch page, top to bottom in this order. "Latest Trending" shows products flagged trending (or the newest ones).</p>
        <div className="space-y-3">
          {banners.map((b, i) => (
            <div key={b.id} data-testid={`banner-row-${b.id}`} className={`rounded-xl border-2 ${b.is_active ? "border-zinc-200" : "border-dashed border-zinc-300 opacity-70"} p-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center`}>
              <BannerPreview b={b} deptName={deptName(b.department_id)} />
              <div className="flex sm:flex-col gap-1.5 items-center sm:items-stretch px-1">
                <div className="text-[10px] text-zinc-500 text-center">{b.kind === "trending" ? "Latest Trending" : deptName(b.department_id) || "—"} · {b.size}</div>
                {scheduleState(b) && <span data-testid={`banner-schedule-${b.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-center ${scheduleState(b).cls}`}><CalendarClock className="w-3 h-3 inline mr-0.5" />{scheduleState(b).label}</span>}
                <div className="flex items-center gap-1.5 justify-center"><Switch data-testid={`banner-toggle-${b.id}`} checked={b.is_active} onCheckedChange={() => toggleBanner(b)} /><span className="text-[10px]">{b.is_active ? "Live" : "Hidden"}</span></div>
                <div className="flex gap-1 justify-center">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveBanner(i, -1)} disabled={i === 0} data-testid={`banner-up-${b.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveBanner(i, 1)} disabled={i === banners.length - 1} data-testid={`banner-down-${b.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid={`banner-edit-${b.id}`} onClick={() => openBanner(b)}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600" data-testid={`banner-delete-${b.id}`} onClick={() => removeBanner(b)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
          {banners.length === 0 && <p className="text-xs text-zinc-500">No banners — add one to bring the merch page to life.</p>}
        </div>
      </Card>

      <Dialog open={bannerOpen} onOpenChange={setBannerOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit banner" : "New banner"}</DialogTitle>
            <DialogDescription>Colors, name and department. Changes are live as soon as you save.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveBanner} className="space-y-4" data-testid="banner-form">
            <BannerPreview b={form} deptName={deptName(form.department_id)} />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Banner name</Label>
                <Input data-testid="banner-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Mens" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Subtitle</Label>
                <Input data-testid="banner-subtitle-input" value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1" placeholder="Shop mens" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Opens</Label>
                <select data-testid="banner-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                  <option value="trending">Latest Trending</option>
                  <option value="department">A department</option>
                </select>
              </div>
              {form.kind === "department" && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Department</Label>
                  <select data-testid="banner-department-select" value={form.department_id || ""} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                    <option value="">Pick one…</option>
                    {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Size</Label>
                <select data-testid="banner-size-select" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                  <option value="full">Full width</option>
                  <option value="half">Half width</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="banner-active-switch" checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">{form.is_active ? "Live" : "Hidden"}</span>
              </div>
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Schedule (optional)</Label>
              <p className="text-[10px] text-zinc-500 mb-2">Leave blank to always show. Set dates for seasonal drops — the banner appears and disappears automatically (Central time).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Show from</Label>
                  <Input data-testid="banner-starts-input" type="date" value={form.starts_at || ""} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Show until</Label>
                  <Input data-testid="banner-ends-input" type="date" value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Color scheme</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-3">
                {PRESETS.map((p) => (
                  <button key={p.name} type="button" data-testid={`banner-preset-${p.name}`} onClick={() => setForm({ ...form, bg_color: p.bg_color, bg_color_2: p.bg_color_2, text_color: p.text_color, accent_color: p.accent_color })} className="text-[11px] font-bold px-2.5 py-1 rounded-full border-2 border-zinc-200 hover:border-[#0C2E2B]" style={{ background: `linear-gradient(120deg, ${p.bg_color}, ${p.bg_color_2})`, color: p.text_color }}>{p.name}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ColorField label="Background" value={form.bg_color} onChange={(v) => setForm({ ...form, bg_color: v })} testId="banner-bg-color" />
                <ColorField label="Background 2" value={form.bg_color_2} onChange={(v) => setForm({ ...form, bg_color_2: v })} testId="banner-bg2-color" />
                <ColorField label="Text" value={form.text_color} onChange={(v) => setForm({ ...form, text_color: v })} testId="banner-text-color" />
                <ColorField label="Accent" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} testId="banner-accent-color" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Photo — the advertising hook for this department</Label>
              <p className="text-[10px] text-zinc-500 mb-2">Upload a product/lifestyle shot or paste an image URL. Use a wide photo (at least 1200px) for best results.</p>
              <ImageUploader value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} testId="banner-image-uploader" />
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-2">
                <Input data-testid="banner-image-url-input" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="…or paste an image URL (https://…)" className="h-9 text-xs font-mono-data" />
                <select data-testid="banner-image-style-select" value={form.image_style || "side"} onChange={(e) => setForm({ ...form, image_style: e.target.value })} className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-xs">
                  <option value="side">Photo on the right</option>
                  <option value="cover">Photo fills the banner</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setBannerOpen(false)}>Cancel</Button>
              <Button data-testid="banner-save-btn" type="submit" className="btn-kinetic rounded-md flex-1">{editing ? "Save banner" : "Add banner"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
